import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);
const STORAGE_KEY = "oht_token";

function setAuthHeader(token) {
  if (token) {
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common["Authorization"];
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem(STORAGE_KEY) || null);
  const [loading, setLoading] = useState(true);

  // On mount: if token exists, attach header and fetch /me to verify
  useEffect(() => {
    (async () => {
      const t = localStorage.getItem(STORAGE_KEY);
      if (t) {
        setAuthHeader(t);
        try {
          const res = await axios.get(`${API}/auth/me`);
          setUser(res.data);
          setToken(t);
        } catch {
          localStorage.removeItem(STORAGE_KEY);
          setAuthHeader(null);
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    })();
  }, []);

  const login = useCallback(async (username, password) => {
    const res = await axios.post(`${API}/auth/login`, { username, password });
    const { token: t, user: u } = res.data;
    localStorage.setItem(STORAGE_KEY, t);
    setAuthHeader(t);
    setToken(t);
    setUser(u);
    return u;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setAuthHeader(null);
    setToken(null);
    setUser(null);
  }, []);

  const refreshMe = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
      return res.data;
    } catch { return null; }
  }, []);

  const isAdmin = user?.role === "admin";

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout, refreshMe, isAdmin }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
